import {
  Document,
  Paragraph,
  Packer,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  BorderStyle,
} from 'docx';
import { abbreviation } from '../abbreviation';
import type {
  ParticipantExportDataOutputType as ParticipantData,
  IvrCallDetailOutputType as IvrCall,
  IvrQuestionAnswerOutputType as QuestionAnswer,
} from '../../graphql';

interface SymptomItem {
  symptom: string;
  value: number;
}

interface Summary {
  severe: SymptomItem[];
  moderate: SymptomItem[];
  none: SymptomItem[];
  skipped: SymptomItem[];
}

/* ---------- helpers ---------- */
const createP = (text: string, bold = false, alignment: 'left' | 'center' | 'right' = 'left') =>
  new Paragraph({
    children: [new TextRun({ text, bold })],
    alignment,
  });

const createTitle = (text: string) =>
  new Paragraph({
    children: [new TextRun({ text, bold: true, size: 36 })],
    alignment: 'left',
    spacing: { after: 400 },
  });

const padId = (id: number) => id.toString().padStart(5, '0');

const today = () =>
  new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });

const isLooseStool = (qa: QuestionAnswer) => {
  const text = qa.questionText.toLowerCase();
  const { questionDetails } = qa;
  const symptomName = questionDetails?.symptomName?.toLowerCase() || '';

  return (
    symptomName.includes('stool') ||
    text.includes('loose') ||
    text.includes('watery stool') ||
    text.includes('loose stool')
  );
};

const wantQ = (qa: QuestionAnswer) => {
  const text = qa.questionText.toLowerCase();
  const { questionDetails } = qa;

  if (questionDetails?.symptomName) {
    const isFreq = /frequency|often/i.test(text);
    const isSev = /severity|worst|much.*interfere|interfere.*much/i.test(text);

    if (isLooseStool(qa)) {
      return isFreq || (!isSev && !isFreq);
    }
    return isSev || (!isSev && !isFreq);
  }

  const symptomKeywords = /pain|headache|nausea|fatigue|fever|cough|breath|sleep|appetite|mood/i;
  return symptomKeywords.test(text);
};

const nameOf = (qa: QuestionAnswer) => {
  const { questionDetails } = qa;
  return (
    questionDetails?.symptomName ||
    questionDetails?.identifier ||
    qa.questionText.replace(/^[^.]*\.\s*/, '').slice(0, 60)
  );
};

const summariseCall = (call: IvrCall): Summary => {
  const out: Summary = { severe: [], moderate: [], none: [], skipped: [] };
  const done = new Set<string>();

  for (const qa of call.questionsAndAnswers) {
    if (!wantQ(qa)) continue;

    const value = Number.parseInt(qa.answerValue || '0', 10);
    if (Number.isNaN(value)) continue;

    const symptomName = nameOf(qa);
    const lowerName = symptomName.toLowerCase();

    if (done.has(lowerName)) continue;
    done.add(lowerName);

    const item = { symptom: symptomName, value };

    if (value === 9) {
      out.skipped.push(item);
    } else if (value >= 3) {
      out.severe.push(item);
    } else if (value >= 1) {
      out.moderate.push(item);
    } else {
      out.none.push(item);
    }
  }

  return out;
};

const createSymptomTable = (
  items: SymptomItem[],
  title: string,
  bgColor: string,
  textColor = '000000',
) => {
  if (items.length === 0) {
    return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: title, bold: true, color: 'FFFFFF' })],
                }),
              ],
              shading: { fill: bgColor },
              width: { size: 70, type: WidthType.PERCENTAGE },
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: 'Score', bold: true, color: 'FFFFFF' })],
                }),
              ],
              shading: { fill: bgColor },
              width: { size: 30, type: WidthType.PERCENTAGE },
            }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({
              children: [new Paragraph('None reported')],
              columnSpan: 2,
              shading: { fill: 'F8F9FA' },
            }),
          ],
        }),
      ],
    });
  }

  const headerRow = new TableRow({
    children: [
      new TableCell({
        children: [
          new Paragraph({
            children: [new TextRun({ text: title, bold: true, color: 'FFFFFF' })],
          }),
        ],
        shading: { fill: bgColor },
        width: { size: 70, type: WidthType.PERCENTAGE },
      }),
      new TableCell({
        children: [
          new Paragraph({
            children: [new TextRun({ text: 'Score', bold: true, color: 'FFFFFF' })],
          }),
        ],
        shading: { fill: bgColor },
        width: { size: 30, type: WidthType.PERCENTAGE },
      }),
    ],
  });

  const dataRows = items.map(
    (item, index) =>
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: `${index + 1}. ${item.symptom}`, color: textColor }),
                ],
              }),
            ],
            shading: { fill: index % 2 === 0 ? 'FFFFFF' : 'F8F9FA' },
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: item.value.toString(), bold: true, color: textColor }),
                ],
              }),
            ],
            shading: { fill: index % 2 === 0 ? 'FFFFFF' : 'F8F9FA' },
          }),
        ],
      }),
  );

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [headerRow, ...dataRows],
  });
};

/* ---------- DOCX ---------- */
export async function generateSymptomReportDocx(data: ParticipantData[]): Promise<Blob> {
  if (data.length === 0) {
    throw new Error('No participant data available');
  }

  const allDocumentChildren: (Paragraph | Table)[] = [];
  const studyHeader = data[0]?.studyName?.trim() || 'SYMON NRG-CC012CD';

  // Add main title once
  allDocumentChildren.push(
    createTitle(studyHeader),
    createP('Provider Symptom Report', true, 'left'),
    createP(`Report Generated: ${today()}`, false, 'left'),
    createP(''),
  );

  // Process each participant
  for (const [participantIndex, pt] of data.entries()) {
    const initials = abbreviation(pt.participantName);
    const externalId = pt.participantExternalId;

    if (pt.ivrCalls.length === 0) {
      allDocumentChildren.push(
        createP(`Patient Initials: ${initials}`, true),
        createP(`Participant ID: ${externalId}`, false),
        createP('No IVR data available', false),
        createP(''),
      );
      continue;
    }

    const call = pt.ivrCalls[0]!;
    const summary = summariseCall(call);
    const callDate = new Date(call.callDate).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });

    allDocumentChildren.push(
      createP(`Patient Initials: ${initials}`, true),
      createP(`Participant ID: ${externalId}`, false),
      createP(`Symptom Call Date: ${callDate}`, false),
      createP(''),
      // Severe Symptoms - Red
      createSymptomTable(summary.severe, 'Severe Symptoms (3-4)', 'DC3545', '000000'),
      createP(''),
      // Moderate Symptoms - Orange
      createSymptomTable(summary.moderate, 'Moderate Symptoms (1-2)', 'FD7E14', '000000'),
      createP(''),
      // No Symptoms - Green
      createSymptomTable(summary.none, 'No Symptoms Reported (0)', '198754', 'FFFFFF'),
      createP(''),
      // Skipped - Gray
      createSymptomTable(summary.skipped, 'N/A or Skipped (9)', '6C757D', 'FFFFFF'),
      createP(''),
    );
  }

  const document_ = new Document({
    sections: [{ children: allDocumentChildren }],
  });

  return Packer.toBlob(document_);
}

/* ---------- CSV ---------- */
export function generateSymptomReportCSV(data: ParticipantData[]): Blob {
  if (data.length === 0) {
    throw new Error('No participant data available');
  }

  const rows: string[] = [];

  for (const pt of data) {
    const initials = abbreviation(pt.participantName);
    const studyCode = pt.studyName?.replace(/\s+/g, '') || 'CC012CD';
    const paddedId = padId(pt.participantId);
    const participantCode = `${studyCode}-${paddedId}`;

    if (pt.ivrCalls.length === 0) {
      rows.push(`${participantCode},${initials},,No IVR data available`);
      continue;
    }

    const call = pt.ivrCalls[0]!;
    const summary = summariseCall(call);
    const callDate = new Date(call.callDate).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });

    // Create a single row for this participant
    const symptomData: string[] = [];

    // Add all symptoms with their scores
    for (const item of [
      ...summary.severe,
      ...summary.moderate,
      ...summary.none,
      ...summary.skipped,
    ]) {
      symptomData.push(`"${item.symptom}":"${item.value}"`);
    }

    rows.push(`${participantCode},${initials},${callDate},"${symptomData.join(';')}"`);
  }

  return new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
}

/* ---------- filename helper ---------- */
export function symptomReportFilename(data: ParticipantData[], format: 'docx' | 'csv'): string {
  if (data.length === 0) {
    return `symptom-report.${format}`;
  }

  const pt = data[0]!;
  const today = new Date()
    .toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    })
    .replace(/\//g, '.');

  const studyCode = pt.studyName?.replace(/\s+/g, '') || 'CC012CD';
  const paddedId = padId(pt.participantId);

  if (data.length > 1) {
    return `${studyCode}-Multiple-Participants Symptom Report ${today}.${format}`;
  }

  return `${studyCode}-${paddedId} Symptom Report ${today}.${format}`;
}
